import { z } from 'zod'
import { Event } from './answer.js'
import { InstantieId, PublicatieId } from './id.js'
import { Person } from './moment.js'

/**
 * De verklaring van de koppelvlakdienst bij een handeling zonder mens (besluit van 1 november 2026,
 * ontwerp 7.8 voorstel 1 en 2, R-83, R-84, R-154).
 *
 * Een machinale indiening, overdracht of oordeel gaat van het processysteem langs de koppelvlakdienst
 * naar de pod, en van de pod naar de opslagdienst. Daar is geen token van een gebruiker. Tot deze datum
 * toetste de opslagdienst op die weg alleen dat de persoon die de pod noemde een levende toekenning op
 * het dossier had (`withoutToken` in `apps/store/src/toekenningen.ts`): een gekaapte pod kon zo iedereen
 * noemen die er een toekenning had. Nu ondertekent de koppelvlakdienst per handeling een korte verklaring
 * met wie er handelt, op welke instantie van welke publicatie, en wat; de pod geeft haar ongewijzigd door
 * in {@link MACHINE_STATEMENT_HEADER}, en de opslagdienst kijkt haar na en legt haar naast het verzoek.
 * Een pod heeft de sleutel niet en kan dus geen verklaring maken, alleen een echte doorgeven, en die
 * geldt voor één handeling, één keer (`jti`), en kort.
 *
 * **ES256, zoals de tokens van de inlogdienst** (`TOKEN_ALGORITHM` in `identity.ts`): één
 * algoritme in het product, met `kid` in de kop zodat een sleutel te roteren is. De koppelvlakdienst
 * heeft het private deel (`KOPPELVLAK_VERKLARING_SLEUTEL`), de opslagdienst alleen de publieke delen
 * (`STORE_KOPPELVLAK_VERKLARING_SLEUTELS`), zonder standaardwaarde.
 */

/** De header waarin de koppelvlakdienst de verklaring aan de pod geeft, en de pod haar aan de opslagdienst. */
export const MACHINE_STATEMENT_HEADER = 'x-eformulieren-verklaring'

/** De uitgever van een verklaring: de koppelvlakdienst. */
export const MACHINE_STATEMENT_ISSUER = 'eformulieren-koppelvlak'

/** Voor wie een verklaring is: de opslagdienst. Een token van de inlogdienst heeft een andere audience. */
export const MACHINE_STATEMENT_AUDIENCE = 'eformulieren-opslag'

/**
 * Hoe lang een verklaring geldig is, in seconden: twee minuten. Een handeling van de koppelvlakdienst
 * duurt één verzoek; de pod schrijft daarbinnen de sectiedocumenten weg en rekent de controles na, en dat
 * past ruim in deze tijd. De opslagdienst weigert een vervaltijd die verder vooruit ligt.
 */
export const MACHINE_STATEMENT_VALIDITY_S = 120

/** Wat een verklaring toelaat: een revisie met deze gebeurtenis, of een oordeel op een criterium. */
export const MachineAction = z.enum([...Event.options, 'oordeel'])
export type MachineAction = z.infer<typeof MachineAction>

/**
 * De claims van een verklaring. `iss` en `aud` kijkt de verifieerder na met de constanten hierboven.
 *
 * **`z.object` en niet `strictObject`**: de koppelvlakdienst en de opslagdienst worden los van elkaar
 * uitgerold, en een claim die de koppelvlakdienst later toevoegt mag een oudere opslagdienst niet laten
 * weigeren. Wat hier staat is verplicht.
 */
export const MachineStatementClaims = z
  .object({
    /** Eenmalig: de opslagdienst neemt elke `jti` één keer aan op een schrijfroute. */
    jti: z.string().min(16).max(128),
    iat: z.number().int().positive(),
    exp: z.number().int().positive(),
    publicatie: PublicatieId,
    instantie: InstantieId,
    /** Wie er handelt, zoals het processysteem het zei (`<handelend><persoon>`). */
    persoon: Person,
    /** Namens wie (R-190, R-191), of `null`. */
    namens: Person.nullable(),
    handeling: MachineAction,
    /** Het criterium, alleen bij een oordeel. */
    criterium: z.string().min(1).optional(),
  })
  .refine((claims) => (claims.handeling === 'oordeel') === (claims.criterium !== undefined), {
    message: 'een oordeel noemt een criterium, en alleen een oordeel',
    path: ['criterium'],
  })
export type MachineStatementClaims = z.infer<typeof MachineStatementClaims>
